"use client"

import Link from "next/link"
import { fireAnalyticsEvent as track } from "@/lib/analytics"
import "./final-cta.css"

/**
 * Final CTA section — port of the Claude Design export
 * `Final CTA Section.dc.html` (Velova Hero Visual v2 Restructure (4)). The
 * page's closing conversion block: a connector narrowing out of the Actions
 * section into one input (the visitor's own brand), the two approved CTAs,
 * and a compact FAQ that folds in the old standalone FAQ section.
 *
 * Styling lives in the scoped ./final-cta.css (fonts remapped to the app's
 * next/font CSS variables — no new font requests). The FAQ uses native
 * <details>/<summary>, so the accordion needs no JS; this component is a
 * client component only to fire the CTA analytics events that the rest of
 * the site's CTAs use.
 *
 * Export deltas: the export's fake "tail of the Actions section" preview
 * block and "Footer begins here" marker are dropped (the real Actions
 * section precedes this, and the real footer follows). The "Which AI
 * platforms" answer replaces the export's build-note placeholder with the
 * accurate supported-surface list. CTAs point at real routes.
 */

type Faq = { q: string; a: string; open?: boolean }

// Column 1 leads with the open item; the export splits 3 / 2 across two columns.
const FAQ_COLS: Faq[][] = [
  [
    {
      q: "What does the free test include?",
      a: "A focused view of how your brand appears across relevant AI answers, where competitors are being selected and which initial gaps may deserve attention.",
      open: true,
    },
    {
      q: "Which AI platforms does Velova track?",
      a: "Velova tracks leading AI-answer surfaces including ChatGPT, Gemini, Google AI Mode, Google AI Overviews, Perplexity and Claude. Included coverage varies by plan — the Pricing page lists every entitlement.",
    },
    {
      q: "What is a Velova Action?",
      a: "An Action is a prioritised issue or opportunity backed by captured AI answers, competitor patterns, website findings and source gaps. Velova also prepares practical work your team can review.",
    },
  ],
  [
    {
      q: "Does Velova publish changes automatically?",
      a: "No. Your team reviews, edits and approves prepared work before anything is implemented or published.",
    },
    {
      q: "How is this different from traditional SEO?",
      a: "SEO helps your website perform in conventional search. Velova also examines how your brand is represented, compared, cited and recommended inside AI-generated answers.",
    },
  ],
]

function FaqItem({ item }: { item: Faq }) {
  return (
    <details className="cta-faq-item" open={item.open}>
      <summary>
        <span className="cta-faq-q">{item.q}</span>
        <span aria-hidden className="cta-faq-icon">+</span>
      </summary>
      <p className="cta-faq-a">{item.a}</p>
    </details>
  )
}

export function FinalCta() {
  return (
    <section aria-labelledby="cta-headline" className="velova-final-cta section-default">
      <div aria-hidden className="cta-glow" />

      <div className="velova-container">

      {/* the Actions connector narrows into one input: the visitor's own brand */}
      <div aria-hidden className="cta-connector">
        <svg viewBox="0 0 320 72" preserveAspectRatio="none" focusable="false">
          <path d="M20 0 C20 40 152 32 160 72" fill="none" stroke="rgba(178,224,240,.28)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d="M110 0 C110 44 154 40 160 72" fill="none" stroke="rgba(178,224,240,.34)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d="M210 0 C210 44 166 40 160 72" fill="none" stroke="rgba(178,224,240,.34)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <path d="M300 0 C300 40 168 32 160 72" fill="none" stroke="rgba(178,224,240,.28)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        </svg>
      </div>

      <header className="cta-header">
        <div className="cta-eyebrow">
          <span aria-hidden className="cta-gem" />
          <span className="cta-eyebrow-label">Start with your brand</span>
        </div>
        <h2 id="cta-headline" className="cta-headline">
          See where AI finds you—and where it doesn&rsquo;t.
        </h2>
        <p className="cta-subline">Your first growth opportunities are already inside the answers.</p>
        <p className="cta-support">
          Run a free test to see how your brand appears across leading AI platforms, which
          competitors are being chosen and where Velova finds opportunities to improve.
        </p>
      </header>

      <div className="cta-form">
        <label className="cta-inputwrap">
          <span className="cta-inputlabel">Domain</span>
          <span aria-hidden className="cta-inputdiv" />
          <input type="text" placeholder="yourbrand.com" aria-label="Your domain" className="cta-input" />
        </label>

        <div className="cta-buttons">
          <Link
            href="/signup"
            onClick={() => track("cta_start_free", { page: "home", section: "final_cta" })}
            className="cta-btn cta-btn-primary"
          >
            Run a free test
            <span aria-hidden className="cta-btn-arrow">→</span>
          </Link>
          <Link href="/pricing" className="cta-btn cta-btn-secondary">
            View pricing
            <span aria-hidden className="cta-btn-arrow">→</span>
          </Link>
        </div>

        <p className="cta-microcopy">
          No credit card required. Review your initial visibility findings before choosing a plan.
        </p>
      </div>

      {/* compact FAQ, secondary to the conversion block */}
      <div className="cta-faq">
        <div className="cta-faq-head">
          <span className="cta-faq-label">Before you start</span>
          <span aria-hidden className="cta-faq-rule" />
        </div>
        <div className="cta-faq-grid">
          {FAQ_COLS.map((col, i) => (
            <div key={i} className="cta-faq-col">
              {col.map((item) => (
                <FaqItem key={item.q} item={item} />
              ))}
            </div>
          ))}
        </div>
      </div>
      </div>
    </section>
  )
}
